import { useCallback, useEffect, useState } from 'react';

import { useDebounce } from '~/hooks/useDebounce';
import { OrganizationsPageQuery, useOrganizationsPageQuery } from '~/types/api.graphql';

const PAGE_ITEMS_COUNT = 10;

export type OrganizationNode = OrganizationsPageQuery['organizationsPage']['nodes'][number];

export const useOrganizationsPagedList = () => {
  const [items, setItems] = useState<OrganizationNode[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTextLocal, setSearchTextLocal] = useState('');
  const searchQuery = useDebounce(searchTextLocal, 500) as string;

  // Declared before the data effect so a search change clears stale rows before the next page result is appended.
  useEffect(() => {
    setPage(1);
    setItems([]);
    setHasMore(false);
  }, [searchQuery]);

  const {
    refetch,
    loading: loadingFirst,
    data: orgsQueryData,
  } = useOrganizationsPageQuery({
    variables: {
      pageNumber: page,
      pageItemsCount: PAGE_ITEMS_COUNT,
      searchQuery,
    },
    fetchPolicy: 'network-only',
    skip: page > 1,
  });

  const applyOrgsPage = useCallback((dataOrgs: OrganizationsPageQuery) => {
    setItems((prevItems) => [...prevItems, ...(dataOrgs?.organizationsPage?.nodes || [])]);
    setHasMore(dataOrgs?.organizationsPage?.pageInfo?.hasNextPage);
    setPage((prevPage) => prevPage + 1);
  }, []);

  useEffect(() => {
    if (!orgsQueryData) return;
    applyOrgsPage(orgsQueryData);
    // Run once per Apollo data object; applyOrgsPage changes can re-append the same page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgsQueryData]);

  const loadingFirstPage = loadingFirst && !items.length;

  const handleLoadMore = () => {
    if (!hasMore || loadingFirstPage || loadingMore) return;
    void (async () => {
      setLoadingMore(true);
      try {
        const result = await refetch({ pageNumber: page, pageItemsCount: PAGE_ITEMS_COUNT, searchQuery });
        if (result.data) applyOrgsPage(result.data);
      } finally {
        setLoadingMore(false);
      }
    })();
  };

  return {
    items,
    hasMore,
    loadingMore,
    loadingFirstPage,
    searchTextLocal,
    setSearchTextLocal,
    handleLoadMore,
  };
};
